import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAtom } from 'jotai';
import moment from 'moment';
import XLSX from 'xlsx';
import { apiUrl } from '../db/company';
import { jobOpenCommitmentsAtom } from '../store/company';
import { Chip } from './procurementFilters';
import { chaseRows } from './billingInvoiceChase';

// Billing › Third Party › Lien Waivers: every vendor invoice JDE has vouchered
// on the job, and where its lien waiver stands — when we asked for it, each
// follow-up, and when it came in (MEPC §11.11 makes sub and supplier waivers a
// condition of payment).
//
// The list and the trail live server-side (functions lienWaivers.js:
// PC.Lien_Waiver_Event, folded by PC.v_Lien_Waiver_Status), so AP's UiPath bot
// reads and logs the same rows through api/lienWaiverApi — a request the bot
// emails shows here as "Requested · UiPath", and one logged here is one the bot
// won't send again. Nothing is deleted: a step logged by mistake is undone
// (voided) and stays in the history, struck through.
//
// The client invoice column comes from this page's own Bill Invoice fold
// (chaseRows): which of our summaries already carry the vendor invoice's cost.
// Billed with no waiver in is the case to chase first.
//
// Pure helpers first (tested on their own), then the view.

export const EVENT_TYPES = ['Requested', 'Follow-up', 'Received', 'Not required'];
export const WAIVER_TYPES = ['Conditional progress', 'Unconditional progress', 'Conditional final', 'Unconditional final'];

export const isWaiting = (w) => w.status === 'Requested' || w.status === 'Followed up';

/** The step a row's main button logs next. */
export const nextStep = (w) => (w.status === 'Not requested' ? 'Requested' : isWaiting(w) ? 'Follow-up' : null);

/** Status filter chips, in order; `test` null = everything. */
export const FILTERS = [
    { k: 'all', label: 'All', test: null },
    { k: 'not', label: 'Not requested', test: (w) => w.status === 'Not requested' },
    { k: 'waiting', label: 'Waiting', test: isWaiting },
    { k: 'due', label: 'Follow-up due', test: (w) => w.followUpDue },
    { k: 'uncond', label: 'Unconditional due', test: (w) => w.unconditionalDue },
    { k: 'billed', label: 'Billed, no waiver', test: (w) => w.billedNoWaiver },
    { k: 'received', label: 'Received', test: (w) => w.status === 'Received' },
    { k: 'nr', label: 'Not required', test: (w) => w.status === 'Not required' },
];

export const waiverKey = (w) => `${w.job}|${w.supplierAb}|${w.invoiceKey}`;

const upper = (v) => String(v || '').trim().toUpperCase();
const uniq = (xs) => [...new Set(xs.filter(Boolean))];

/**
 * { `${po}|${INVOICE NO}`: [client invoice nos] } from Bill Invoice's PO-line
 * fold — each line's latest vendor invoice and the summaries its cost is on.
 */
export const billedOnMap = (lines = []) => {
    const m = {};
    lines.forEach((l) => {
        if (!l.invNo || !(l.billedOn || []).length) return;
        const k = `${String(l.po).replace(/^0+(?=\d)/, '')}|${upper(l.invNo)}`;
        m[k] = uniq([...(m[k] || []), ...l.billedOn]);
    });
    return m;
};

/** The API's rows plus what this page knows: which client invoices carry each one. */
export const withBilledOn = (waivers = [], map = {}) => waivers.map((w) => {
    const billedOn = uniq((w.poNumbers || []).flatMap((po) => map[`${po}|${w.invoiceKey}`] || []));
    return { ...w, billedOn, billedNoWaiver: billedOn.length > 0 && w.status !== 'Received' && w.status !== 'Not required' };
});

// What needs a hand first: follow-ups due, unconditional waivers owed, billed
// with nothing in, then never asked, then waiting, then done — newest voucher
// first within each.
const rank = (w) => (w.followUpDue ? 0 : w.unconditionalDue ? 1 : w.billedNoWaiver ? 2
    : w.status === 'Not requested' ? 3 : isWaiting(w) ? 4 : w.status === 'Received' ? 5 : 6);
export const sortWaivers = (list) => list.slice().sort((a, b) => rank(a) - rank(b)
    || String(b.voucherDate || '').localeCompare(String(a.voucherDate || ''))
    || String(a.supplierName).localeCompare(String(b.supplierName))
    || String(a.invoiceKey).localeCompare(String(b.invoiceKey)));

export const matchesSearch = (w, q) => {
    const s = String(q || '').trim().toLowerCase();
    if (!s) return true;
    return [w.supplierName, w.supplierAb, w.invoiceNo, ...(w.poNumbers || []), ...(w.billedOn || []), w.status]
        .some((v) => String(v ?? '').toLowerCase().includes(s));
};

export const waiverStats = (list = []) => ({
    total: list.length,
    notRequested: list.filter((w) => w.status === 'Not requested').length,
    waiting: list.filter(isWaiting).length,
    followUpDue: list.filter((w) => w.followUpDue).length,
    unconditionalDue: list.filter((w) => w.unconditionalDue).length,
    billedNoWaiver: list.filter((w) => w.billedNoWaiver).length,
    received: list.filter((w) => w.status === 'Received').length,
    notRequired: list.filter((w) => w.status === 'Not required').length,
});

/** Events by waiverKey, newest first (the API already sorts them). */
export const eventsByKey = (events = []) => events.reduce((m, e) => {
    const k = `${e.job}|${e.supplierAb}|${e.invoiceKey}`;
    (m[k] = m[k] || []).push(e);
    return m;
}, {});

// ── API ──

const call = async (fn, data) => {
    try {
        const res = await fetch(`${apiUrl}api/${fn}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok || body.error) return { error: body.error || `The server answered ${res.status}` };
        return body;
    } catch (e) {
        return { error: e?.message || String(e) };
    }
};

// ── View ──

const money = (n) => (n == null ? '' : (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
const md = (iso) => (iso ? moment(iso, 'YYYY-MM-DD').format('M/D/YY') : '');
const stamp = (iso) => (iso ? moment(iso).format('M/D/YY h:mm A') : '');

const th = {
    fontSize: 12, fontWeight: 500, color: 'var(--muted-foreground)', textAlign: 'left', background: 'var(--subtle)',
    borderBottom: '1px solid var(--border)', padding: '8px 10px', whiteSpace: 'nowrap',
};
const td = { fontSize: 14, padding: '8px 10px', borderBottom: '1px solid var(--border)', color: 'var(--foreground)', verticalAlign: 'top' };
const sub = { fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2, whiteSpace: 'nowrap' };
const num = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const dash = <span style={{ color: 'var(--muted-foreground)' }}>—</span>;

const btn = {
    font: 'inherit', fontSize: 13, fontWeight: 500, height: 28, padding: '0 10px', borderRadius: 'var(--radius)',
    border: '1px solid var(--border)', background: 'var(--background)', color: 'var(--foreground)',
    boxShadow: 'var(--shadow-xs)', cursor: 'pointer', whiteSpace: 'nowrap',
};
const rowBtn = { ...btn, height: 26, fontSize: 12, padding: '0 8px' };
const btnPrimary = { ...btn, background: 'var(--primary)', borderColor: 'var(--primary)', color: 'var(--primary-foreground)', boxShadow: 'none' };
const field = {
    font: 'inherit', fontSize: 14, height: 32, padding: '0 10px', borderRadius: 'var(--radius)',
    border: '1px solid var(--input)', background: 'var(--background)', color: 'var(--foreground)', width: '100%',
};
const label = { fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' };

const Pill = ({ tone, children, title }) => {
    const t = tone === 'good' ? { bg: 'var(--success-tint)', fg: 'var(--success-text)' }
        : tone === 'warn' ? { bg: 'var(--warning-tint)', fg: 'var(--warning-text)' }
            : tone === 'bad' ? { bg: 'var(--destructive-tint)', fg: 'var(--destructive-text)' }
                : tone === 'info' ? { bg: 'var(--primary-tint)', fg: 'var(--primary)' }
                    : { bg: 'var(--muted)', fg: 'var(--text-secondary)' };
    return (
        <span title={title} style={{ display: 'inline-flex', alignItems: 'center', padding: '1px 8px', borderRadius: 'var(--radius-pill)', fontSize: 12, fontWeight: 500, background: t.bg, color: t.fg, whiteSpace: 'nowrap', marginRight: 4, marginBottom: 2 }}>
            {children}
        </span>
    );
};

const STATUS_TONE = { 'Not requested': 'neutral', Requested: 'info', 'Followed up': 'info', Received: 'good', 'Not required': 'neutral' };

const Stat = ({ label: l, value, tone }) => (
    <div style={{ paddingRight: 20, marginRight: 20, borderRight: '1px solid var(--border)' }}>
        <div style={{ fontSize: 12, color: 'var(--muted-foreground)', whiteSpace: 'nowrap' }}>{l}</div>
        <div style={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: tone || 'var(--foreground)' }}>{value}</div>
    </div>
);

const today = () => moment().format('YYYY-MM-DD');

// The form behind every "Log" button: what happened, when, with whom, and —
// for a received waiver — its form, amount and the signed copy's link.
const LogDialog = ({ waiver, initialType, userEmail, onClose, onSaved }) => {
    const [form, setForm] = useState({
        eventType: initialType || nextStep(waiver) || 'Received',
        eventDate: today(),
        contact: '',
        waiverType: waiver.unconditionalDue ? 'Unconditional progress' : 'Conditional progress',
        waiverAmount: waiver.invoiceAmount != null ? String(waiver.invoiceAmount) : '',
        docUrl: '',
        note: '',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const set = (k) => (ev) => setForm({ ...form, [k]: ev.target.value });
    const received = form.eventType === 'Received';

    useEffect(() => {
        const onKey = (ev) => { if (ev.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const save = async () => {
        if (!userEmail) {
            setError("Fusion hasn't loaded who you are yet, so this can't be logged under your name. Reload the page and try again.");
            return;
        }
        setSaving(true);
        setError('');
        const res = await call('saveLienWaiverEvent', {
            job: waiver.job,
            supplierAb: waiver.supplierAb,
            supplierName: waiver.supplierName,
            invoiceNo: waiver.invoiceNo,
            eventType: form.eventType,
            eventDate: form.eventDate,
            contact: form.contact,
            note: form.note,
            ...(received ? { waiverType: form.waiverType, waiverAmount: form.waiverAmount, docUrl: form.docUrl } : {}),
            user: userEmail,
        });
        setSaving(false);
        if (res.error) setError(res.error);
        else onSaved();
    };

    // On document.body: the Billing page's scroll area would otherwise clip
    // the fixed overlay to its own box.
    return createPortal(
        <div role="presentation" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgb(0 0 0 / 0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Log a lien waiver step"
                onClick={(ev) => ev.stopPropagation()}
                style={{ background: 'var(--popover)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-md)', width: 480, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}
            >
                <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>Log lien waiver</div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                        {`${waiver.supplierName || `Supplier ${waiver.supplierAb}`} · invoice ${waiver.invoiceNo}${waiver.invoiceAmount != null ? ` · ${money(waiver.invoiceAmount)}` : ''}`}
                    </div>
                </div>
                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', rowGap: 12 }}>
                    <div>
                        <span style={label}>What happened</span>
                        <div role="group" aria-label="What happened" style={{ display: 'inline-flex', gap: 2, padding: 3, background: 'var(--muted)', borderRadius: 'var(--radius)', flexWrap: 'wrap' }}>
                            {EVENT_TYPES.map((t) => (
                                <button
                                    key={t}
                                    type="button"
                                    aria-pressed={form.eventType === t}
                                    onClick={() => setForm({ ...form, eventType: t })}
                                    style={{
                                        font: 'inherit', fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 'var(--radius-inner)', border: '1px solid transparent', cursor: 'pointer',
                                        background: form.eventType === t ? 'var(--primary)' : 'transparent',
                                        color: form.eventType === t ? 'var(--primary-foreground)' : 'var(--text-secondary)',
                                    }}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div style={{ display: 'flex', columnGap: 12 }}>
                        <label style={{ flex: '0 0 160px' }}>
                            <span style={label}>Date</span>
                            <input type="date" value={form.eventDate} max={today()} onChange={set('eventDate')} style={field} />
                        </label>
                        <label style={{ flex: 1 }}>
                            <span style={label}>{received ? 'Sent by' : 'Asked of'}</span>
                            <input type="text" value={form.contact} onChange={set('contact')} placeholder="Name or email" style={field} />
                        </label>
                    </div>
                    {received ? (
                        <>
                            <div style={{ display: 'flex', columnGap: 12 }}>
                                <label style={{ flex: 1 }}>
                                    <span style={label}>Waiver form</span>
                                    <select value={form.waiverType} onChange={set('waiverType')} style={field}>
                                        {WAIVER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </label>
                                <label style={{ flex: '0 0 150px' }}>
                                    <span style={label}>Amount</span>
                                    <input type="text" inputMode="decimal" value={form.waiverAmount} onChange={set('waiverAmount')} style={{ ...field, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} />
                                </label>
                            </div>
                            <label>
                                <span style={label}>Link to the signed waiver</span>
                                <input type="url" value={form.docUrl} onChange={set('docUrl')} placeholder="https://strikeusa.sharepoint.com/…" style={field} />
                            </label>
                        </>
                    ) : null}
                    <label>
                        <span style={label}>Note</span>
                        <textarea value={form.note} onChange={set('note')} rows={2} style={{ ...field, height: 'auto', padding: '6px 10px', resize: 'vertical' }} />
                    </label>
                    {error ? (
                        <div role="alert" style={{ fontSize: 13, background: 'var(--destructive-tint)', color: 'var(--destructive-text)', border: '1px solid color-mix(in srgb, var(--destructive) 25%, transparent)', borderRadius: 'var(--radius)', padding: '8px 10px' }}>
                            {error}
                        </div>
                    ) : null}
                </div>
                <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', columnGap: 8 }}>
                    <button type="button" onClick={onClose} style={btn}>Cancel</button>
                    <button type="button" onClick={save} disabled={saving || !form.eventDate} style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>
                        {saving ? 'Saving…' : `Log ${form.eventType.toLowerCase()}`}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

// The trail under an opened row, newest first, each with an Undo, and a
// button for any step (an unconditional waiver after a conditional one, a
// request after "not required").
const History = ({ events, onVoid, onLog }) => (
    <div style={{ display: 'flex', flexDirection: 'column', rowGap: 4, padding: '4px 0 4px 14px' }}>
        <div><button type="button" onClick={onLog} style={{ ...btn, height: 26, fontSize: 12, marginBottom: 4 }}>Log a step…</button></div>
        {events.length ? events.map((e) => (
            <div key={e.eventId} style={{ display: 'flex', alignItems: 'baseline', columnGap: 10, fontSize: 13, color: e.voided ? 'var(--muted-foreground)' : 'var(--foreground)' }}>
                <span style={{ width: 64, fontVariantNumeric: 'tabular-nums', textDecoration: e.voided ? 'line-through' : 'none' }}>{md(e.eventDate)}</span>
                <span style={{ width: 92, fontWeight: 500, textDecoration: e.voided ? 'line-through' : 'none' }}>{e.eventType}</span>
                <span style={{ flex: 1, minWidth: 0, textDecoration: e.voided ? 'line-through' : 'none' }}>
                    {[e.contact, e.waiverType, e.waiverAmount != null ? money(e.waiverAmount) : null, e.note].filter(Boolean).join(' · ')}
                    {e.docUrl ? <> · <a href={e.docUrl} target="_blank" rel="noreferrer">waiver</a></> : null}
                </span>
                <span style={{ ...sub, marginTop: 0 }} title={stamp(e.createdAt)}>
                    {`${e.source && e.source !== 'Fusion' && e.createdBy !== e.source ? `${e.source} · ${e.createdBy}` : e.createdBy}${e.voided ? ` · undone by ${e.voidedBy}` : ''}`}
                </span>
                {!e.voided ? (
                    <button type="button" onClick={() => onVoid(e)} style={{ ...btn, height: 24, fontSize: 12, padding: '0 8px', boxShadow: 'none' }}>Undo</button>
                ) : <span style={{ width: 50 }} />}
            </div>
        )) : <div style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>Nothing logged yet.</div>}
    </div>
);

const exportRow = (w) => ({
    Job: w.job,
    Vendor: w.supplierName,
    'Vendor #': w.supplierAb,
    'Vendor Invoice #': w.invoiceNo,
    'Invoice Date': w.invoiceDate || '',
    PO: (w.poNumbers || []).join(', '),
    'Invoice Amount': w.invoiceAmount ?? '',
    'Job Amount': w.jobAmount ?? '',
    'Pay Status': w.payStatus || '',
    'Paid Date': w.paidDate || '',
    Checks: (w.checkNumbers || []).join(', '),
    'Client Invoice': (w.billedOn || []).join(', '),
    'Waiver Status': w.status,
    Requested: w.requestedDate || '',
    'Follow-ups': w.followUpCount,
    'Last Follow-up': w.lastFollowUpDate || '',
    'Days Waiting': w.daysWaiting ?? '',
    'Follow-up Due': w.followUpDue ? 'Yes' : '',
    Received: w.receivedDate || '',
    'Waiver Form': w.waiverType || '',
    'Waiver Amount': w.waiverAmount ?? '',
    'Unconditional Due': w.unconditionalDue ? 'Yes' : '',
    'Waiver Link': w.docUrl || '',
    'Last Logged By': w.lastEventBy || '',
});

const BillingLienWaivers = ({ jobKey, userEmail, poStatus, txns, invAmtByKey }) => {
    const [openCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [data, setData] = useState({ waivers: [], events: [], followUpDays: 7 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(null);
    const [logFor, setLogFor] = useState(null);
    const [reload, setReload] = useState(0);

    useEffect(() => {
        if (!jobKey) return undefined;
        let live = true;
        setLoading(true);
        setError('');
        call('readLienWaivers', { job: jobKey }).then((res) => {
            if (!live) return;
            setLoading(false);
            if (res.error) setError(res.error);
            else setData({ waivers: res.waivers || [], events: res.events || [], followUpDays: res.followUpDays || 7 });
        });
        return () => { live = false; };
    }, [jobKey, reload]);

    const billedOn = useMemo(
        () => billedOnMap(chaseRows({ poStatus, txns, openCommitments, today: today(), invAmtByKey })),
        [poStatus, txns, openCommitments, invAmtByKey],
    );
    const all = useMemo(() => sortWaivers(withBilledOn(data.waivers, billedOn)), [data.waivers, billedOn]);
    const byKey = useMemo(() => eventsByKey(data.events), [data.events]);
    const stats = waiverStats(all);
    const test = (FILTERS.find((f) => f.k === filter) || FILTERS[0]).test;
    const rows = all.filter((w) => (!test || test(w)) && matchesSearch(w, search));
    // Hidden on a job with no Fusion billing summaries (54026071), where it
    // would be a column of dashes.
    const showBilled = all.some((w) => w.billedOn.length);
    const cols = showBilled ? 10 : 9;

    const undo = async (e) => {
        if (!window.confirm(`Undo the ${e.eventType.toLowerCase()} logged for ${md(e.eventDate)}? It stays in the history, struck through.`)) return;
        const res = await call('voidLienWaiverEvent', { eventId: e.eventId, user: userEmail });
        if (res.error) setError(res.error);
        else setReload((n) => n + 1);
    };

    const exportXlsx = () => {
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.map(exportRow)), 'Lien Waivers');
        XLSX.writeFile(wb, `Lien_Waivers_${jobKey}_${today()}.xlsx`);
    };

    return (
        <div data-testid="lien-waivers" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-xs)', padding: '16px 18px', marginBottom: 16, width: 'max-content', minWidth: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', columnGap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 15, fontWeight: 600 }}>Lien waivers</span>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)', maxWidth: 640 }}>
                    {`Every vendor invoice JDE has vouchered on this job. A request with no reply for ${data.followUpDays} days is due a follow-up. AP's UiPath bot reads and logs the same list.`}
                </span>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: 8, margin: '12px 0' }}>
                <Stat label="Vendor invoices" value={stats.total} />
                <Stat label="Not requested" value={stats.notRequested} />
                <Stat label="Waiting" value={stats.waiting} />
                <Stat label="Follow-up due" value={stats.followUpDue} tone={stats.followUpDue ? 'var(--warning-text)' : undefined} />
                <Stat label="Billed, no waiver" value={stats.billedNoWaiver} tone={stats.billedNoWaiver ? 'var(--destructive-text)' : undefined} />
                <Stat label="Received" value={stats.received} tone={stats.received ? 'var(--success-text)' : undefined} />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 8, marginBottom: 8 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {FILTERS.map((f) => (
                        <Chip key={f.k} label={f.k === 'all' ? f.label : `${f.label} (${all.filter(f.test).length})`} active={filter === f.k} onPress={() => setFilter(f.k)} />
                    ))}
                </div>
                <input
                    type="search"
                    value={search}
                    onChange={(ev) => setSearch(ev.target.value)}
                    placeholder="Vendor, invoice #, PO"
                    aria-label="Search lien waivers"
                    style={{ ...field, width: 220, marginBottom: 6 }}
                />
                <button type="button" onClick={exportXlsx} disabled={!rows.length} style={{ ...btn, height: 32, marginBottom: 6 }}>Export to Excel</button>
                <button type="button" onClick={() => setReload((n) => n + 1)} style={{ ...btn, height: 32, marginBottom: 6 }}>Refresh</button>
            </div>

            {error ? (
                <div role="alert" style={{ fontSize: 13, background: 'var(--destructive-tint)', color: 'var(--destructive-text)', border: '1px solid color-mix(in srgb, var(--destructive) 25%, transparent)', borderRadius: 'var(--radius)', padding: '8px 10px', marginBottom: 8 }}>
                    {`Lien waivers didn't load: ${error}`}
                </div>
            ) : null}

            {loading && !all.length ? (
                <div style={{ fontSize: 13, color: 'var(--muted-foreground)', padding: '4px 0 12px' }}>Loading lien waivers…</div>
            ) : error && !all.length ? null : (
                <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={th}>Vendor</th>
                                <th style={th}>Invoice</th>
                                <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                                <th style={th}>Paid</th>
                                {showBilled ? <th style={th}>Client invoice</th> : null}
                                <th style={th}>Waiver</th>
                                <th style={th}>Requested</th>
                                <th style={th}>Follow-ups</th>
                                <th style={th}>Received</th>
                                <th style={th}>Log</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length ? rows.map((w) => {
                                const k = waiverKey(w);
                                const isOpen = open === k;
                                const step = nextStep(w);
                                return (
                                    <React.Fragment key={k}>
                                        <tr
                                            onClick={() => setOpen(isOpen ? null : k)}
                                            title={isOpen ? 'Hide the history' : 'Show the history'}
                                            style={{ cursor: 'pointer', background: isOpen ? 'var(--subtle)' : undefined }}
                                        >
                                            <td style={td}>
                                                <button
                                                    type="button"
                                                    aria-expanded={isOpen}
                                                    onClick={(ev) => { ev.stopPropagation(); setOpen(isOpen ? null : k); }}
                                                    style={{ font: 'inherit', fontSize: 14, fontWeight: 600, color: 'var(--foreground)', textAlign: 'left', background: 'transparent', border: '1px solid transparent', boxShadow: 'none', padding: 0, cursor: 'pointer', maxWidth: 220 }}
                                                >
                                                    <span style={{ display: 'inline-block', width: 14, color: 'var(--muted-foreground)', fontSize: 11 }}>{isOpen ? '▾' : '▸'}</span>
                                                    {w.supplierName || `Supplier ${w.supplierAb}`}
                                                </button>
                                                <div style={{ ...sub, paddingLeft: 14 }}>{`#${w.supplierAb}`}</div>
                                            </td>
                                            <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                                {w.invoiceNo}
                                                <div style={sub}>
                                                    {[md(w.invoiceDate), (w.poNumbers || []).length ? `PO ${w.poNumbers.join(', ')}` : ''].filter(Boolean).join(' · ')}
                                                </div>
                                                {!w.inJde ? <div style={sub} title="Logged against an invoice JDE doesn't list as vouchered on this job">not in JDE</div> : null}
                                            </td>
                                            <td style={{ ...td, ...num }}>
                                                {w.invoiceAmount != null ? money(w.invoiceAmount) : dash}
                                                {w.jobAmount != null && w.invoiceAmount != null && Math.abs(w.jobAmount - w.invoiceAmount) >= 1 ? (
                                                    <div style={sub} title="This job's PO lines on the invoice, before tax">{`${money(w.jobAmount)} on job`}</div>
                                                ) : null}
                                            </td>
                                            <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                                {w.payStatus === 'Paid' || w.payStatus === 'Partly paid' ? (
                                                    <>
                                                        {`${w.payStatus} ${md(w.paidDate)}`}
                                                        {(w.checkNumbers || []).length ? <div style={sub}>{`check ${w.checkNumbers.join(', ')}`}</div> : null}
                                                    </>
                                                ) : w.payStatus ? (
                                                    <>
                                                        <span style={{ color: 'var(--text-secondary)' }}>Not paid</span>
                                                        {w.dueDate ? <div style={sub}>{`due ${md(w.dueDate)}`}</div> : null}
                                                    </>
                                                ) : dash}
                                            </td>
                                            {showBilled ? (
                                                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                                    {w.billedOn.length ? w.billedOn.join(', ') : dash}
                                                </td>
                                            ) : null}
                                            <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                                <Pill tone={STATUS_TONE[w.status]}>{w.status}</Pill>
                                                {w.daysWaiting != null ? <span style={{ ...sub, marginTop: 0 }}>{`${w.daysWaiting} day${w.daysWaiting === 1 ? '' : 's'}`}</span> : null}
                                                {w.followUpDue ? <div><Pill tone="warn" title={`Nothing back since ${md(w.lastTouchDate)}`}>Follow-up due</Pill></div> : null}
                                                {w.unconditionalDue ? <div><Pill tone="warn" title="The conditional waiver is in and JDE shows the invoice paid">Unconditional due</Pill></div> : null}
                                                {w.billedNoWaiver ? <div><Pill tone="bad" title="Its cost is on a client invoice, and its waiver isn't in">Billed, no waiver</Pill></div> : null}
                                            </td>
                                            <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                                {w.requestedDate ? md(w.requestedDate) : dash}
                                            </td>
                                            <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                                {w.followUpCount ? `${w.followUpCount}×` : dash}
                                                {w.lastFollowUpDate ? <div style={sub}>{`last ${md(w.lastFollowUpDate)}`}</div> : null}
                                            </td>
                                            <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                                {w.receivedDate ? md(w.receivedDate) : w.status === 'Not required' ? <span style={{ color: 'var(--text-secondary)' }}>{`not required ${md(w.notRequiredDate)}`}</span> : dash}
                                                {w.waiverType ? <div style={sub}>{w.waiverType}</div> : null}
                                                {w.docUrl ? <div style={sub}><a href={w.docUrl} target="_blank" rel="noreferrer" onClick={(ev) => ev.stopPropagation()}>Open waiver</a></div> : null}
                                            </td>
                                            <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={(ev) => ev.stopPropagation()}>
                                                <div style={{ display: 'flex', columnGap: 6 }}>
                                                    {step ? <button type="button" onClick={() => setLogFor({ waiver: w, type: step })} style={rowBtn}>{step}</button> : null}
                                                    {isWaiting(w) ? (
                                                        <button type="button" onClick={() => setLogFor({ waiver: w, type: 'Received' })} style={rowBtn}>Received</button>
                                                    ) : null}
                                                </div>
                                            </td>
                                        </tr>
                                        {isOpen ? (
                                            <tr style={{ background: 'var(--subtle)' }}>
                                                <td colSpan={cols} style={{ ...td, paddingTop: 4 }}>
                                                    <History events={byKey[k] || []} onVoid={undo} onLog={() => setLogFor({ waiver: w, type: null })} />
                                                </td>
                                            </tr>
                                        ) : null}
                                    </React.Fragment>
                                );
                            }) : (
                                <tr>
                                    <td colSpan={cols} style={{ ...td, color: 'var(--muted-foreground)', borderBottom: 0 }}>
                                        {all.length ? 'No vendor invoices match.' : 'JDE has no vouchered vendor invoices on this job.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {logFor ? (
                <LogDialog
                    waiver={logFor.waiver}
                    initialType={logFor.type}
                    userEmail={userEmail}
                    onClose={() => setLogFor(null)}
                    onSaved={() => { setLogFor(null); setOpen(waiverKey(logFor.waiver)); setReload((n) => n + 1); }}
                />
            ) : null}
        </div>
    );
};

export default BillingLienWaivers;
